import { useCallback } from "react";
import { filterCombosForProperty } from "../utils/roomRateCombos";

/**
 * Custom hook for RoomsRateplansDrawer handlers
 * Extracts handler logic to reduce component size
 */
export function useRoomsRateplansDrawerHandlers({
  properties,
  propertyOptions,
  selectedHotelInDrawer,
  setSelectedHotelInDrawer,
  selectedProperty,
  setDrawerRatePlans,
  setDrawerRooms,
  setDrawerDataPropertyId,
  drawerSelections,
  promo,
  onFieldChange,
  selectedProperties,
  setSuccessMessage,
  savedSelectionsBeforeDrawerRef,
  onClose,
  getRatePlansByProperty,
  getRoomsByProperty,
  buildAllRoomRateplanCombos,
  roomsRateplansByPropertyRef,
}) {
  const handleHotelChange = useCallback(
    async (value) => {
      // Selections are deliberately NOT carried over when the hotel changes.
      // Per-property selections persist only when the user presses Save; an
      // unsaved edit is discarded and that hotel falls back to all of its
      // combos (handleConfirm's backfill below). Auto-persisting here would
      // commit selections the user never confirmed for that hotel.
      //
      // The dropdown's `value` IS the propertyId, so fetch with it directly.
      // Don't gate on `properties.find(...)`: that list only holds the first
      // page of the catalog (20), so switching to a property beyond it would
      // not resolve and its Rooms/Rate Plans would never load.
      setSelectedHotelInDrawer(value || null);

      if (!value) {
        return;
      }

      try {
        // Fetch rate plans and rooms for the selected property
        const [ratePlansRes, roomsRes] = await Promise.all([
          getRatePlansByProperty(value),
          getRoomsByProperty(value),
        ]);

        const ratePlansData = ratePlansRes?.data?.data || [];
        const roomsData = roomsRes?.data?.data || [];

        setDrawerRatePlans(ratePlansData);
        setDrawerRooms(roomsData);
        if (setDrawerDataPropertyId) {
          setDrawerDataPropertyId(value);
        }

        // Restore saved selections for this property, or use all combinations
        // as default. Combos are tagged with this hotel so they can never be
        // submitted under a different one.
        const savedSelections = filterCombosForProperty(
          promo?.allowedRateRoomCodesByProperty?.[value],
          value
        );

        if (savedSelections.length > 0) {
          // Use saved selections if they exist
          onFieldChange("allowedRateRoomCodes", savedSelections);
        } else {
          // If no saved selections, default to all combinations
          const allCombos = buildAllRoomRateplanCombos(
            roomsData,
            ratePlansData,
            value
          );
          onFieldChange("allowedRateRoomCodes", allCombos);
        }
      } catch {
        // In case of API failure, keep existing data; no user-visible change required here.
      }
    },
    [
      setSelectedHotelInDrawer,
      setDrawerRatePlans,
      setDrawerRooms,
      setDrawerDataPropertyId,
      promo?.allowedRateRoomCodesByProperty,
      onFieldChange,
      getRatePlansByProperty,
      getRoomsByProperty,
      buildAllRoomRateplanCombos,
    ]
  );

  const handleDeselectAll = useCallback(() => {
    // Deselect all - click the master checkbox to deselect all
    const masterCheckbox = document.querySelector(
      ".rooms-rateplans button[aria-label='Select or deselect all rooms and rate plans']"
    );
    if (masterCheckbox) {
      // Check if all are currently selected by checking aria-pressed attribute
      const isAllSelected =
        masterCheckbox.getAttribute("aria-pressed") === "true";
      if (isAllSelected) {
        // All are selected, click once to deselect all
        masterCheckbox.click();
      } else {
        // Not all are selected, click to select all first, then click again to deselect
        masterCheckbox.click();
        // Use setTimeout to wait for React state update before second click
        setTimeout(() => {
          masterCheckbox.click();
        }, 100);
      }
    }
  }, []);

  const handleSelectAll = useCallback(() => {
    // Select all - click the master checkbox to select all green ticks
    const masterCheckbox = document.querySelector(
      ".rooms-rateplans button[aria-label='Select or deselect all rooms and rate plans']"
    );
    if (masterCheckbox) {
      // Check if all are currently selected by checking aria-pressed attribute
      const isAllSelected =
        masterCheckbox.getAttribute("aria-pressed") === "true";
      if (!isAllSelected) {
        // Not all are selected, click to select all
        masterCheckbox.click();
      }
    }
  }, []);

  const handleSave = useCallback(
    (selectedCombo) => {
      // Save logic - store per-property room/rate plan combinations
      const currentPropertyId =
        selectedHotelInDrawer || selectedProperty?.propertyId;

      if (!currentPropertyId) {
        return;
      }

      // Get existing per-property data or initialize
      const existingByProperty = promo?.allowedRateRoomCodesByProperty || {};

      // Update the data for current property with the selected combinations
      const updatedByProperty = {
        ...existingByProperty,
        [currentPropertyId]: selectedCombo,
      };

      // Save to promo object - this is the source of truth for each property
      onFieldChange("allowedRateRoomCodesByProperty", updatedByProperty);

      // Update the main allowedRateRoomCodes to reflect current property's selections
      // This is needed for RoomsRateplans to display the correct state
      onFieldChange("allowedRateRoomCodes", selectedCombo);

      // Update snapshot so Close won't revert this Save
      savedSelectionsBeforeDrawerRef.current = [...selectedCombo];

      // Name for the success message. Prefer the dropdown's own option label:
      // it resolves every property the picker can offer, including ones beyond
      // the first catalog page that `properties` never holds.
      const option = propertyOptions?.find(
        (o) => o.value === currentPropertyId
      );
      const property = properties?.find(
        (p) => p.propertyId === currentPropertyId
      );
      const propertyName =
        option?.label || property?.propertyName || "the selected property";

      // Set success message to display in banner
      setSuccessMessage(
        `Rooms and Rate Plan selections for ${propertyName} have been updated.`
      );
    },
    [
      selectedHotelInDrawer,
      selectedProperty?.propertyId,
      promo?.allowedRateRoomCodesByProperty,
      onFieldChange,
      savedSelectionsBeforeDrawerRef,
      properties,
      propertyOptions,
      setSuccessMessage,
    ]
  );

  // Retrieve cached property data or fetch from API
  const getOrFetchPropertyData = useCallback(
    async (propId) => {
      const cached = roomsRateplansByPropertyRef?.current?.[propId];
      if (cached) return cached;

      const [ratePlansRes, roomsRes] = await Promise.all([
        getRatePlansByProperty(propId),
        getRoomsByProperty(propId),
      ]);
      const data = {
        rooms: roomsRes?.data?.data || [],
        ratePlans: ratePlansRes?.data?.data || [],
      };
      if (roomsRateplansByPropertyRef?.current) {
        roomsRateplansByPropertyRef.current[propId] = data;
      }
      return data;
    },
    [roomsRateplansByPropertyRef, getRatePlansByProperty, getRoomsByProperty]
  );

  const handleConfirm = useCallback(async () => {
    // Commit drawer selections to the promo state
    onFieldChange("allowedRateRoomCodes", drawerSelections);

    if (selectedProperties.length) {
      // Multiple properties: Also save to allowedRateRoomCodesByProperty
      const currentPropertyId =
        selectedHotelInDrawer || selectedProperty?.propertyId;

      const existingByProperty = promo?.allowedRateRoomCodesByProperty || {};
      const updatedByProperty = { ...existingByProperty };

      // Update current property with drawer selections
      if (currentPropertyId) {
        updatedByProperty[currentPropertyId] = drawerSelections;
      }

      // Default unvisited properties to all room/rateplan combos
      const missingProperties = selectedProperties.filter(
        (propId) =>
          !updatedByProperty[propId] || updatedByProperty[propId].length === 0
      );

      for (const propId of missingProperties) {
        try {
          const data = await getOrFetchPropertyData(propId);
          updatedByProperty[propId] = buildAllRoomRateplanCombos(
            data.rooms,
            data.ratePlans,
            propId
          );
        } catch {
          continue;
        }
      }

      // Save to promo object
      onFieldChange("allowedRateRoomCodesByProperty", updatedByProperty);
    }

    setSuccessMessage("Rooms and Rate Plan selections have been confirmed.");

    // Clear snapshot and close drawer
    savedSelectionsBeforeDrawerRef.current = null;
    onClose();
  }, [
    onFieldChange,
    drawerSelections,
    selectedProperties,
    selectedHotelInDrawer,
    selectedProperty?.propertyId,
    promo?.allowedRateRoomCodesByProperty,
    setSuccessMessage,
    savedSelectionsBeforeDrawerRef,
    onClose,
    getOrFetchPropertyData,
    buildAllRoomRateplanCombos,
  ]);

  return {
    handleHotelChange,
    handleDeselectAll,
    handleSelectAll,
    handleSave,
    handleConfirm,
  };
}
